import React from 'react';

/**
 * Canonical legal-document metadata footer.
 * Renders Version · Effective · Last updated · Locale from the
 * Agreement Library metadata. Replaces every hard-coded
 * "Last updated: Check document version" string.
 *
 * Never renders internal migration IDs, publisher identity, engine
 * notes, content keys, or SHAs. If the required fields are missing,
 * renders a neutral notice — the Legal Readiness Validator will keep
 * the environment RED.
 */

const LOCALE_LABELS = { en: 'English', tr: 'Türkçe' };

function fmt(dateStr) {
  if (!dateStr) return null;
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return null;
    return d.toISOString().slice(0, 10);
  } catch {
    return null;
  }
}

export const LegalMetadata = ({ content, className = '' }) => {
  if (!content) return null;

  const version = content.version ?? null;
  const effective = fmt(content.effectiveFrom ?? content.effective_from);
  const published = fmt(content.publishedAt ?? content.published_at);
  const updated = fmt(content.updatedAt ?? content.updated_at);
  const locale = content.resolvedLanguage ?? content.resolved_language ?? null;
  const changelog = content.changelog ?? null;

  const rows = [];
  if (version != null) rows.push({ label: 'Version', value: String(version), testid: 'legal-meta-version' });
  if (effective)       rows.push({ label: 'Effective from', value: effective, testid: 'legal-meta-effective' });
  if (published)       rows.push({ label: 'Published', value: published, testid: 'legal-meta-published' });
  if (updated)         rows.push({ label: 'Last updated', value: updated, testid: 'legal-meta-updated' });
  if (locale)          rows.push({ label: 'Language', value: LOCALE_LABELS[locale] || locale.toUpperCase(), testid: 'legal-meta-locale' });

  if (rows.length === 0) {
    return (
      <div
        className={`border-t mt-8 pt-4 text-xs text-gray-400 ${className}`}
        data-testid="legal-metadata-unavailable"
      >
        Document metadata is being finalised. Please check back soon.
      </div>
    );
  }

  return (
    <div
      className={`border-t mt-8 pt-4 text-xs text-slate-500 ${className}`}
      data-testid="legal-metadata"
    >
      <dl className="grid grid-cols-2 gap-x-6 gap-y-1 sm:grid-cols-3 md:grid-cols-5">
        {rows.map((r) => (
          <div key={r.label} className="flex flex-col" data-testid={r.testid}>
            <dt className="text-[10px] uppercase tracking-wide text-slate-400">{r.label}</dt>
            <dd className="text-slate-700">{r.value}</dd>
          </div>
        ))}
      </dl>
      {changelog && (
        <div className="mt-3 text-slate-500" data-testid="legal-meta-changelog">
          <span className="text-[10px] uppercase tracking-wide text-slate-400">
            What changed
          </span>
          <div className="mt-0.5">{changelog}</div>
        </div>
      )}
    </div>
  );
};

export default LegalMetadata;
